/* eslint-disable react/prop-types */
import React from "react";
import PropTypes from "prop-types";
// @material-ui/core
import withStyles from "@material-ui/core/styles/withStyles";

// core components
import GridItem from "components/Grid/GridItem.jsx";
import GridContainer from "components/Grid/GridContainer.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import CardFooter from "components/Card/CardFooter.jsx";
import Button from "components/CustomButtons/Button.jsx";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts";
import Table from "components/Table/Table.jsx";

import NavPills from "components/NavPills/NavPills.js";

import {
  generateDeviceMonthlyReport,
  getDeviceReports,
  getMonthlyReportStatus,
  getUserDevices,
  recalculateDeviceReports
} from "../../services/dashboardDataProvider";

import "react-circular-progressbar/dist/styles.css";
import { CircularProgressbar } from "react-circular-progressbar";
// import { MEDIA_ROOT } from "config.js";

import bulbIcon from "assets/img/bulb.webp";
import cflIcon from "assets/img/CFL.png";
import acIcon from "assets/img/air-conditioner.webp";
import fanIcon from "assets/img/fan.jpg";
import fridgeIcon from "assets/img/fridge.webp";
import pumpIcon from "assets/img/pump.png";
import motorIcon from "assets/img/motor.jpeg";

import { dayjs, DATE_DISPLAY_FORMAT_DETAILED } from "utils/time.jsx";
import { API_ROOT } from "config.js";
import { isDarkUiTheme } from "theme/deviceDetailsTheme.js";
import { getReportChartUi } from "theme/reportChartTheme.js";

const loadIcons = {
  Bulb: bulbIcon,
  CFL: cflIcon,
  AC: acIcon,
  Fan: fanIcon,
  Refrigerator: fridgeIcon,
  "Water pump": pumpIcon,
  "Agriculture Machine": motorIcon
};

function formatDateInput(date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function getDefaultDaySelection() {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return formatDateInput(yesterday);
}

function getDefaultWeekStartSelection() {
  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - 7);
  return formatDateInput(weekStart);
}

function getDefaultMonthSelection() {
  const current = new Date();
  const year = current.getFullYear();
  const month = `${current.getMonth() + 1}`.padStart(2, "0");
  return `${year}-${month}`;
}

function resolveReportUrl(url) {
  if (!url) {
    return "";
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  const base = (API_ROOT || "").replace(/\/$/, "");
  const path = String(url).replace(/^\//, "");
  return `${base}/${path}`;
}

function formatNumber(value, fraction = 2) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toFixed(fraction) : (0).toFixed(fraction);
}

const styles = theme => {
  const isDark = isDarkUiTheme(theme);
  const chartUi = getReportChartUi();

  return {
  img: {
    width: "auto",
    height: "auto",
    verticalAlign: "middle",
    border: "0"
  },
  loadOnImg: {
    width: "60px",
    height: "auto",
    backgroundColor: "lightgreen"
  },
  loadOffImg: {
    width: "60px",
    height: "auto"
  },
  cardTitleBlack: {
    color: chartUi.cardTitle,
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "600",
    fontFamily: "'Josefin Sans', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none"
  },
  cardIconTitle: {
    color: chartUi.cardIcon,
    margin: "10px 0 0",
    fontWeight: "300"
  },
  statusSummaryContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between"
  },
  statusSummaryItem: {
    margin: "10px"
  },
  cardIcon: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center"
  },
  summaryPieChart: {
    height: "400px",
    marginBottom: "80px"
  },
  summaryTimeChart: {
    paddingTop: "20px",
    paddingBottom: "20px"
  },
  summaryTrendCharts: {
    paddingTop: "12px",
    paddingBottom: "8px"
  },
  summaryDaysChart: {
    paddingTop: "20px !important",
    paddingBottom: "20px"
  },
  reportHeaderRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "12px"
  },
  reportActions: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: "8px"
  },
  reportActionMessage: {
    margin: 0,
    fontSize: "12px",
    color: chartUi.mutedText
  },
  reportActionError: {
    color: "#c0392b"
  },
  periodSelector: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  periodInput: {
    border: `1px solid ${chartUi.inputBorder}`,
    borderRadius: "8px",
    padding: "5px 8px",
    color: chartUi.inputText,
    background: isDark ? "#1f1f1f" : "transparent"
  },
  periodLabel: {
    fontSize: "12px",
    color: chartUi.mutedText,
    marginRight: "2px"
  },
  actionSelect: {
    minWidth: "220px"
  },
  monthlyReportProgress: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "16px",
    marginTop: "12px",
    padding: "12px 14px",
    borderRadius: "10px",
    border: `1px solid ${chartUi.inputBorder}`,
    background: isDark ? "rgba(31, 31, 31, 0.85)" : "rgba(0, 0, 0, 0.03)",
    width: "100%",
    maxWidth: "520px"
  },
  monthlyReportProgressChart: {
    width: "72px",
    height: "72px",
    flexShrink: 0
  },
  monthlyReportProgressBody: {
    flex: 1,
    minWidth: 0
  },
  monthlyReportProgressTitle: {
    margin: "0 0 4px",
    fontSize: "13px",
    fontWeight: 600,
    color: chartUi.cardTitle,
    textTransform: "capitalize"
  },
  monthlyReportProgressStats: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px 16px",
    margin: "8px 0 0",
    fontSize: "11px",
    color: chartUi.mutedText
  }
};
};

class DeviceReports extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      deviceId: null,
      devices: [],
      reportStatusNames: {},
      selectedDay: getDefaultDaySelection(),
      selectedWeekStart: getDefaultWeekStartSelection(),
      selectedMonth: getDefaultMonthSelection(),
      isRefreshingReports: false,
      refreshMessage: "",
      refreshError: false,
      monthlyReportMessage: "",
      monthlyReportError: false,
      monthlyReportJobId: null,
      monthlyReportStatus: "idle",
      monthlyReportJob: null,
      selectedAction: "view_selected_period"
    };

    this.tabChanged = this.tabChanged.bind(this);
    this.refreshReports = this.refreshReports.bind(this);
    this.viewSelectedPeriod = this.viewSelectedPeriod.bind(this);
    this.handlePeriodSelectionChange = this.handlePeriodSelectionChange.bind(this);
    this.handleActionSelectionChange = this.handleActionSelectionChange.bind(this);
    this.runSelectedAction = this.runSelectedAction.bind(this);
    this.generateMonthlyReport = this.generateMonthlyReport.bind(this);
    this.pollMonthlyReportStatus = this.pollMonthlyReportStatus.bind(this);
    this.clearMonthlyReportPollTimeout = this.clearMonthlyReportPollTimeout.bind(this);
  }

  isAdminUser() {
    const user = this.props.user || {};
    return user.isAdmin === true || user.isSuperuser === true;
  }

  getReportTypeByTab(tabIndex) {
    if (tabIndex === 1) {
      return "week";
    }
    if (tabIndex === 2) {
      return "month";
    }
    return "day";
  }

  loadReportStatusNames(deviceId) {
    return getUserDevices(this.props.axios, { deviceId }).then(data => {
      const reportStatusNames = {};
      (data.available_status_types || []).forEach(statusType => {
        if (statusType.target_type === "report" && statusType.report_period) {
          reportStatusNames[statusType.report_period] = statusType.name;
        }
      });
      this.setState({ reportStatusNames });
    });
  }

  getSelectionForReportType(reportType) {
    if (reportType === "day") {
      return { day: this.state.selectedDay };
    }
    if (reportType === "week") {
      return { weekStart: this.state.selectedWeekStart };
    }
    return { month: this.state.selectedMonth };
  }

  loadReport(deviceId, reportType, tabIndex, options = {}) {
    return getDeviceReports(this.props.axios, deviceId, reportType, options).then(data => {
      this.setState({
        tabIndex: tabIndex !== undefined ? tabIndex : this.state.tabIndex || 0,
        deviceId,
        deviceData: data
      });
    });
  }

  refreshReports(reportTypeOverride, forceRefresh = false) {
    let deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (!deviceId || !this.isAdminUser() || this.state.isRefreshingReports) {
      return;
    }

    const activeTab = this.state.tabIndex || 0;
    const reportType = reportTypeOverride || this.getReportTypeByTab(activeTab);
    const selection = this.getSelectionForReportType(reportType);

    this.setState({
      isRefreshingReports: true,
      refreshMessage: forceRefresh
        ? `Force refreshing ${reportType} report...`
        : `Refreshing ${reportType} report...`,
      refreshError: false
    });

    recalculateDeviceReports(this.props.axios, deviceId, [reportType], {
      ...selection,
      force: forceRefresh
    })
      .then(() => this.loadReportStatusNames(deviceId))
      .then(() => {
        return this.loadReport(deviceId, reportType, activeTab, selection);
      })
      .then(() => {
        this.setState({
          isRefreshingReports: false,
          refreshMessage: forceRefresh
            ? `${reportType} report force-refreshed and updated.`
            : `${reportType} report recalculated and updated.`,
          refreshError: false
        });
      })
      .catch(error => {
        const message =
          (error && error.response && error.response.data && error.response.data.error) ||
          "Failed to refresh reports.";
        this.setState({
          isRefreshingReports: false,
          refreshMessage: message,
          refreshError: true
        });
      });
  }

  handlePeriodSelectionChange(field, value) {
    this.setState({ [field]: value });
  }

  handleActionSelectionChange(value) {
    this.setState({ selectedAction: value });
  }

  runSelectedAction(reportPeriod, showRefreshButton, showMonthlyActions) {
    const action = this.state.selectedAction || "view_selected_period";
    if (action === "view_selected_period") {
      this.viewSelectedPeriod(reportPeriod);
      return;
    }

    if (!showRefreshButton && (action === "refresh_tab" || action === "force_refresh_tab")) {
      return;
    }

    if (!showMonthlyActions && (action === "open_monthly_html" || action === "force_open_monthly_html")) {
      return;
    }

    if (action === "refresh_tab") {
      this.refreshReports(reportPeriod, false);
      return;
    }

    if (action === "force_refresh_tab") {
      this.refreshReports(reportPeriod, true);
      return;
    }

    if (action === "open_monthly_html") {
      this.generateMonthlyReport(false);
      return;
    }

    if (action === "force_open_monthly_html") {
      this.generateMonthlyReport(true);
    }
  }

  viewSelectedPeriod(reportTypeOverride) {
    const deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (!deviceId) {
      return;
    }
    const activeTab = this.state.tabIndex || 0;
    const reportType = reportTypeOverride || this.getReportTypeByTab(activeTab);
    const selection = this.getSelectionForReportType(reportType);
    this.loadReport(deviceId, reportType, activeTab, selection);
  }

  isMonthlyReportInProgress() {
    const status = this.state.monthlyReportStatus;
    return status === "queued" || status === "running";
  }

  formatMissingDaysCount(missingDays) {
    if (Array.isArray(missingDays)) {
      return missingDays.length;
    }
    if (typeof missingDays === "number" && Number.isFinite(missingDays)) {
      return missingDays;
    }
    return 0;
  }

  renderMonthlyReportProgress(classes) {
    const job = this.state.monthlyReportJob;
    if (!job || !this.isMonthlyReportInProgress()) {
      return null;
    }

    const progressPercent = Math.min(
      100,
      Math.max(0, Number(job.progress_percent != null ? job.progress_percent : 0))
    );
    const statusLabel = (job.status || this.state.monthlyReportStatus || "running").replace(/_/g, " ");
    const message =
      job.message || this.state.monthlyReportMessage || "Generating monthly HTML report...";

    return (
      <div className={classes.monthlyReportProgress} role="status" aria-live="polite">
        <div className={classes.monthlyReportProgressChart}>
          <CircularProgressbar value={progressPercent} text={`${progressPercent}%`} />
        </div>
        <div className={classes.monthlyReportProgressBody}>
          <p className={classes.monthlyReportProgressTitle}>{statusLabel}</p>
          <p className={classes.reportActionMessage} style={{ margin: 0 }}>
            {message}
          </p>
          <div className={classes.monthlyReportProgressStats}>
            {job.month ? <span>Month: {job.month}</span> : null}
            {job.daily_reports_generated != null ? (
              <span>Days generated: {job.daily_reports_generated}</span>
            ) : null}
            {job.records_collected != null ? (
              <span>Records: {job.records_collected}</span>
            ) : null}
            {job.missing_days != null ? (
              <span>Missing days: {this.formatMissingDaysCount(job.missing_days)}</span>
            ) : null}
            {job.job_id ? <span>Job: {String(job.job_id).slice(0, 8)}…</span> : null}
          </div>
        </div>
      </div>
    );
  }

  clearMonthlyReportPollTimeout() {
    if (this.monthlyReportPollTimeout) {
      clearTimeout(this.monthlyReportPollTimeout);
      this.monthlyReportPollTimeout = null;
    }
  }

  pollMonthlyReportStatus(deviceId, jobId) {
    getMonthlyReportStatus(this.props.axios, deviceId, jobId)
      .then(job => {
        const nextStatus = job && job.status ? job.status : "running";
        const nextMessage = job && job.message ? job.message : "Generating monthly report...";

        if (nextStatus === "completed") {
          this.clearMonthlyReportPollTimeout();
          this.setState({
            monthlyReportJobId: null,
            monthlyReportStatus: "completed",
            monthlyReportJob: null,
            monthlyReportMessage: job && job.generated
              ? "Monthly report generated successfully."
              : job && job.cached
                ? "Monthly report already exists and was reused."
                : "Monthly report is ready.",
            monthlyReportError: false
          });
          if (job && job.url) {
            window.open(job.url, "_blank", "noopener,noreferrer");
          }
          return;
        }

        if (nextStatus === "failed") {
          this.clearMonthlyReportPollTimeout();
          this.setState({
            monthlyReportJobId: null,
            monthlyReportStatus: "failed",
            monthlyReportJob: job || null,
            monthlyReportMessage: (job && job.error) || nextMessage || "Failed to generate monthly report.",
            monthlyReportError: true
          });
          return;
        }

        this.setState({
          monthlyReportJobId: jobId,
          monthlyReportStatus: nextStatus,
          monthlyReportMessage: nextMessage,
          monthlyReportJob: job,
          monthlyReportError: false
        });

        this.clearMonthlyReportPollTimeout();
        this.monthlyReportPollTimeout = setTimeout(() => {
          this.pollMonthlyReportStatus(deviceId, jobId);
        }, 1500);
      })
      .catch(error => {
        this.clearMonthlyReportPollTimeout();
        const message =
          (error && error.response && error.response.data && error.response.data.error) ||
          "Failed to check monthly report status.";
        this.setState({
          monthlyReportJobId: null,
          monthlyReportStatus: "failed",
          monthlyReportJob: null,
          monthlyReportMessage: message,
          monthlyReportError: true
        });
      });
  }

  generateMonthlyReport(forceRefresh = false) {
    const deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (!deviceId) {
      return;
    }

    this.clearMonthlyReportPollTimeout();
    this.setState({
      monthlyReportMessage: forceRefresh ? "Refreshing monthly report..." : "Generating monthly report...",
      monthlyReportError: false,
      monthlyReportJobId: null,
      monthlyReportStatus: "queued",
      monthlyReportJob: null
    });

    generateDeviceMonthlyReport(this.props.axios, deviceId, {
      force: forceRefresh,
      month: this.state.selectedMonth || new Date().toISOString().slice(0, 7)
    })
      .then(data => {
        if (data && data.job_id) {
          this.setState({
            monthlyReportJobId: data.job_id,
            monthlyReportStatus: data.status || "queued",
            monthlyReportMessage: data.message || "Monthly report generation queued.",
            monthlyReportJob: {
              job_id: data.job_id,
              status: data.status || "queued",
              message: data.message || "Monthly report generation queued.",
              month: data.month || this.state.selectedMonth,
              progress_percent: 0
            }
          });
          this.pollMonthlyReportStatus(deviceId, data.job_id);
          return;
        }

        if (data && data.url) {
          window.open(data.url, "_blank", "noopener,noreferrer");
        }
        this.setState({
          monthlyReportJobId: null,
          monthlyReportStatus: "completed",
          monthlyReportJob: null,
          monthlyReportMessage: forceRefresh
            ? "Monthly report refreshed and reopened."
            : data && data.cached
              ? "Monthly report already exists and was reused."
              : "Monthly report generated successfully.",
          monthlyReportError: false
        });
      })
      .catch(error => {
        const message =
          (error && error.response && error.response.data && error.response.data.error) ||
          "Failed to generate monthly report.";
        this.setState({
          monthlyReportJobId: null,
          monthlyReportStatus: "failed",
          monthlyReportJob: null,
          monthlyReportMessage: message,
          monthlyReportError: true
        });
      });
  }

  componentDidMount() {
    // eslint-disable-next-line react/prop-types
    let deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (deviceId) {
      this.loadReportStatusNames(deviceId);
      this.loadReport(deviceId, "day", 0, this.getSelectionForReportType("day"));
    }
  }

  componentWillUnmount() {
    this.clearMonthlyReportPollTimeout();
  }

  tabChanged(tabIndex) {
    let deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (deviceId) {
      const reportType = this.getReportTypeByTab(tabIndex);
      this.loadReport(deviceId, reportType, tabIndex, this.getSelectionForReportType(reportType));
    }
  }

  renderEnergySummaryChart(reportData) {
    let summaryData = reportData;
    if (
      reportData.per_day_energy_statistics &&
      reportData.per_day_energy_statistics.summary
    ) {
      summaryData = reportData.per_day_energy_statistics.summary;
    }
    const { classes } = this.props;

    function round(num) {
      var m = Number((Math.abs(num) * 100).toPrecision(15));
      return (Math.round(m) / 100) * Math.sign(num);
    }

    let generated = round(
      (summaryData.generated !== undefined
        ? summaryData.generated
        : summaryData.energy_generated) || 0
    );
    let imported = round(
      (summaryData.imported !== undefined
        ? summaryData.imported
        : summaryData.energy_imported) || 0
    );
    let exported = round(
      (summaryData.exported !== undefined
        ? summaryData.exported
        : summaryData.energy_exported) || 0
    );
    let consumed = round(
      (summaryData.consumed !== undefined
        ? summaryData.consumed
        : summaryData.energy_consumed) || 0
    );

    let total_in = generated + imported;

    let options = {
      legend: {
        data: ["Energy In", "Energy Out"]
      },
      radar: {
        // shape: 'circle',
        indicator: [
          { name: "Generated", max: total_in },
          { name: "Imported", max: total_in },
          { name: "Consumed", max: total_in },
          { name: "Exported", max: total_in }
        ]
      },
      series: [
        {
          name: "Energy In vs Energy Out",
          type: "radar",
          data: [
            {
              value: [generated, imported, 0, 0],
              name: "Energy In",
              areaStyle: {
                color: new echarts.graphic.RadialGradient(0.1, 0.6, 1, [
                  {
                    color: "rgba(145, 255, 124, 0.1)",
                    offset: 0
                  },
                  {
                    color: "rgba(145, 255, 124, 0.9)",
                    offset: 1
                  }
                ])
              }
            },
            {
              value: [0, 0, exported, consumed],
              name: "Energy Out",
              areaStyle: {
                color: new echarts.graphic.RadialGradient(0.1, 0.6, 1, [
                  {
                    color: "rgba(255, 145, 124, 0.1)",
                    offset: 0
                  },
                  {
                    color: "rgba(255, 145, 124, 0.9)",
                    offset: 1
                  }
                ])
              }
            }
          ]
        }
      ]
    };
    return (
      <div className={classes.summaryPieChart}>
        <h4>
          <strong>{"Net energy summary (last 7 days):"}</strong>
        </h4>
        <ReactEcharts option={options} />
      </div>
    );
  }

  renderEnergyTimeChart(energyData) {
    const { classes } = this.props;
    let dataToday = [];

    let currentDataPoint = {};
    let lastDataArrivalTime = "";

    energyData.forEach(dataPoint => {
      if (currentDataPoint.data_arrival_time !== lastDataArrivalTime) {
        currentDataPoint = {
          data_arrival_time: dataPoint.data_arrival_time + "+0000"
        };
      }

      if (dataPoint.meter_name === "solar_meter") {
        currentDataPoint["powerGenerated"] = parseFloat(dataPoint.power);
      } else if (dataPoint.meter_name === "load_meter") {
        currentDataPoint["powerConsumed"] = parseFloat(dataPoint.power);
      } else if (dataPoint.meter_name === "grid_meter") {
        currentDataPoint["powerExported"] = -1 * parseFloat(dataPoint.power);
      }

      if (Object.keys(currentDataPoint).length === 4) {
        dataToday.push(currentDataPoint);
        currentDataPoint = {};
      }

      lastDataArrivalTime = dataPoint.data_arrival_time + "+0000";
    });

    const chartUi = getReportChartUi();

    let options = {
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          label: {
            backgroundColor: chartUi.tooltipBg
          }
        }
      },
      legend: {
        data: ["Power generated(W)", "Power consumed(W)", "Power exported(W)"]
      },
      toolbox: {
        feature: {
          saveAsImage: {}
        }
      },
      grid: {
        left: "1%",
        right: "1%",
        bottom: "1%",
        containLabel: true
      },
      xAxis: [
        {
          boundaryGap: false,
          data: dataToday.map(dp => dayjs(dp.data_arrival_time).format("LT"))
        }
      ],
      yAxis: [
        {
          type: "value"
        }
      ],
      series: [
        {
          name: "Power Generated(W)",
          type: "line",
          areaStyle: {},
          emphasis: {
            focus: "series"
          },
          data: dataToday.map(dp => dp.powerGenerated)
        },
        {
          name: "Power consumed(W)",
          type: "line",
          areaStyle: {},
          emphasis: {
            focus: "series"
          },
          data: dataToday.map(dp => dp.powerConsumed)
        },
        {
          name: "Power exported(W)",
          type: "line",
          areaStyle: {},
          emphasis: {
            focus: "series"
          },
          data: dataToday.map(dp => dp.powerExported)
        }
      ]
    };

    return (
      <div className={classes.summaryTimeChart}>
        <h6>
          <strong>{"Generation, Consumption, Export over time"}</strong>
        </h6>
        <ReactEcharts option={options} />
      </div>
    );
  }

  renderReportSummary(reportData) {
    const { classes } = this.props;

    return (
      <div className={classes.reportSummary}>
        <Table
          tableHeaderColor="primary"
          tableData={[
            ["Device", reportData.device],
            ["IP Address", reportData.device_ip_address],
            [
              "Form",
              dayjs(reportData.from_time).format(DATE_DISPLAY_FORMAT_DETAILED)
            ],
            [
              "Till",
              dayjs(reportData.to_time).format(DATE_DISPLAY_FORMAT_DETAILED)
            ],
            [
              "Report Generated At",
              dayjs(reportData.report_generation_time).format(
                DATE_DISPLAY_FORMAT_DETAILED
              )
            ],
            [
              "Data Availability",
              `${(reportData.active_data_days || 0).toFixed(2)}`
            ],
            [
              "Energy Generated",
              (reportData.energy_generated || 0).toFixed(2) + "kWh"
            ],
            [
              "Energy Consumed",
              (reportData.energy_consumed || 0).toFixed(2) + "kWh"
            ],
            [
              "Energy Exported",
              (reportData.energy_exported || 0).toFixed(2) + "kWh"
            ],
            [
              "Energy Imported",
              (reportData.energy_imported || 0).toFixed(2) + "kWh"
            ],
            [
              "Ideal Solar (Period)",
              `${formatNumber(
                reportData.per_day_energy_statistics &&
                  reportData.per_day_energy_statistics.summary &&
                  reportData.per_day_energy_statistics.summary.ideal_solar_kwh,
                2
              )} kWh`
            ],
            [
              "Missed Utilization (Period)",
              `${formatNumber(
                reportData.per_day_energy_statistics &&
                  reportData.per_day_energy_statistics.summary &&
                  reportData.per_day_energy_statistics.summary.missed_util_kwh,
                2
              )} kWh`
            ],
            [
              "CO2 Net (Period)",
              `${formatNumber(
                reportData.per_day_energy_statistics &&
                  reportData.per_day_energy_statistics.summary &&
                  reportData.per_day_energy_statistics.summary.co2_net_kg,
                2
              )} kg`
            ],
            [
              "Per unit rate",
              `${(reportData.consumption_rate || 0).toFixed(2)} ${
                reportData.currency
              }`
            ],
            [
              "Consumption Bill Amount",
              `${(reportData.consumption_bill || 0).toFixed(2)} ${
                reportData.currency
              }`
            ],
            [
              "Money Saved",
              `${(reportData.savings || 0).toFixed(2)} ${reportData.currency}`
            ],
            [
              "Net Bill Amount",
              `${(reportData.net_bill || 0).toFixed(2)} ${reportData.currency}`
            ],
            [
              "Total Investment Amount",
              `${(reportData.total_investment || 0).toFixed(2)} ${
                reportData.currency
              }`
            ],
            [
              "Total Investment Recovered",
              `${(reportData.total_recovery_amount || 0).toFixed(2)} ${
                reportData.currency
              }`
            ]
          ]}
          coloredColls={[3]}
          colorsColls={["primary"]}
        />
      </div>
    );
  }

  renderPeriodInsights(reportData) {
    const { classes } = this.props;
    const insights = reportData.period_insights || {};
    const bestExport = insights.best_export_day || {};
    const highestImport = insights.highest_import_day || {};
    const highestMissed = insights.highest_missed_util_day || {};
    const site = reportData.site || {};
    const plant = reportData.plant || {};

    const linkCell = (record, label) => {
      const resolved = resolveReportUrl(record.report_html_url);
      if (!resolved) {
        return "-";
      }
      return (
        <a href={resolved} target="_blank" rel="noopener noreferrer">
          {label}
        </a>
      );
    };

    return (
      <div className={classes.summaryDaysChart}>
        <h6>
          <strong>Operational Insights</strong>
        </h6>
        <Table
          tableHeaderColor="info"
          tableData={[
            [
              "Site",
              `${site.name || "-"} (${formatNumber(site.lat, 4)}, ${formatNumber(
                site.lon,
                4
              )})`
            ],
            [
              "Plant",
              `${formatNumber(plant.solarDcKw, 1)}kW DC / ${formatNumber(
                plant.inverterAcKw,
                1
              )}kW AC`
            ],
            [
              "Best Export Day",
              bestExport.day
                ? `${bestExport.day} (${formatNumber(bestExport.export_kwh, 2)} kWh)`
                : "-"
            ],
            ["Best Export HTML", linkCell(bestExport, "Open")],
            [
              "Highest Import Day",
              highestImport.day
                ? `${highestImport.day} (${formatNumber(highestImport.import_kwh, 2)} kWh)`
                : "-"
            ],
            ["Highest Import HTML", linkCell(highestImport, "Open")],
            [
              "Highest Missed Utilization",
              highestMissed.day
                ? `${highestMissed.day} (${formatNumber(highestMissed.missed_util_kwh, 2)} kWh)`
                : "-"
            ],
            ["Highest Missed HTML", linkCell(highestMissed, "Open")]
          ]}
          coloredColls={[0]}
          colorsColls={["primary"]}
        />
      </div>
    );
  }

  renderTrendSparklines(reportData) {
    const { classes } = this.props;
    const byTime =
      (reportData.per_day_energy_statistics &&
        reportData.per_day_energy_statistics.by_time) ||
      {};
    const dayKeys = Object.keys(byTime).sort();
    if (!dayKeys.length) {
      return null;
    }

    const labels = dayKeys.map(dayKey => dayjs(dayKey).format("MMM D"));
    const billSeries = dayKeys.map(dayKey =>
      Number((byTime[dayKey] && byTime[dayKey].daily_bill_inr) || 0)
    );
    const co2Series = dayKeys.map(dayKey =>
      Number((byTime[dayKey] && byTime[dayKey].co2_net_kg) || 0)
    );
    const missedSeries = dayKeys.map(dayKey =>
      Number((byTime[dayKey] && byTime[dayKey].missed_util_kwh) || 0)
    );

    const topBillIndexes = [...billSeries]
      .map((value, index) => ({ value, index }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 3)
      .filter(item => item.value > 0)
      .map(item => item.index);

    const highestMissedIndex = missedSeries.length
      ? missedSeries.reduce(
          (bestIndex, currentValue, currentIndex, arr) =>
            currentValue > arr[bestIndex] ? currentIndex : bestIndex,
          0
        )
      : -1;

    const chartUi = getReportChartUi();

    const markerData = [];
    topBillIndexes.forEach(index => {
      markerData.push({
        name: "Top Bill",
        coord: [labels[index], billSeries[index]],
        itemStyle: { color: chartUi.bill }
      });
    });

    if (highestMissedIndex >= 0 && missedSeries[highestMissedIndex] > 0) {
      markerData.push({
        name: "Highest Missed Util",
        coord: [labels[highestMissedIndex], billSeries[highestMissedIndex]],
        itemStyle: { color: chartUi.missed }
      });
    }

    const createSparklineOptions = (title, values, color, markPoints = []) => ({
      title: {
        text: title,
        left: 0,
        textStyle: {
          fontSize: 12,
          fontWeight: 600,
          color: chartUi.title
        }
      },
      tooltip: {
        trigger: "axis"
      },
      grid: {
        top: 32,
        left: 8,
        right: 8,
        bottom: 20,
        containLabel: true
      },
      xAxis: {
        type: "category",
        data: labels,
        boundaryGap: false,
        axisLabel: {
          fontSize: 10,
          color: chartUi.axisLabel
        },
        axisLine: {
          lineStyle: {
            color: chartUi.axisLine
          }
        }
      },
      yAxis: {
        type: "value",
        scale: true,
        splitLine: {
          show: false
        },
        axisLabel: {
          fontSize: 10,
          color: chartUi.axisLabel
        }
      },
      series: [
        {
          type: "line",
          data: values,
          smooth: true,
          showSymbol: false,
          lineStyle: {
            width: 2,
            color
          },
          areaStyle: {
            opacity: 0.1,
            color
          },
          markPoint: {
            symbolSize: 36,
            data: markPoints,
            label: {
              show: true,
              fontSize: 9,
              formatter: point => {
                if (!point || !point.name) {
                  return "";
                }
                return point.name === "Top Bill" ? "B" : "M";
              }
            }
          }
        }
      ]
    });

    return (
      <div className={classes.summaryTrendCharts}>
        <GridContainer>
          <GridItem xs={12} sm={12} md={6}>
            <ReactEcharts
              style={{ height: "180px" }}
              option={createSparklineOptions(
                "Daily Bill Trend (INR)",
                billSeries,
                chartUi.bill,
                markerData
              )}
            />
          </GridItem>
          <GridItem xs={12} sm={12} md={6}>
            <ReactEcharts
              style={{ height: "180px" }}
              option={createSparklineOptions(
                "CO2 Net Trend (kg)",
                co2Series,
                chartUi.co2
              )}
            />
          </GridItem>
        </GridContainer>
      </div>
    );
  }

  renderLastDaysReportTable(reportData) {
    const { classes } = this.props;
    const reportTypes = ["day", "week", "month"];
    const reportCount = ["8", "", ""];

      let tableData = Object.keys(reportData).map(key => {
      const reportUrl = resolveReportUrl(reportData[key]["report_html_url"]);
      return [
      [key],
      [(reportData[key]["solar_meter"] || 0).toFixed(2)],
      [(reportData[key]["load_meter"] || 0).toFixed(2)],
      [(reportData[key]["import_energy_meter"] || 0).toFixed(2)],
      [(reportData[key]["export_energy_meter"] || 0).toFixed(2)],
      [formatNumber(reportData[key]["ideal_solar_kwh"], 2)],
      [formatNumber(reportData[key]["missed_util_kwh"], 2)],
      [formatNumber(reportData[key]["daily_bill_inr"], 1)],
      [formatNumber(reportData[key]["co2_net_kg"], 2)],
      [
        reportUrl ? (
          <a
            href={reportUrl}
            target="_blank"
            rel="noopener noreferrer"
            title={reportData[key]["report_html_path"] || reportUrl}
          >
            Open HTML
          </a>
        ) : (
          "-"
        )
      ]
      ];
    });

    let summaryData = {
      solar_meter: 0,
      load_meter: 0,
      import_energy_meter: 0,
      export_energy_meter: 0
    };

    Object.keys(reportData).forEach(key => {
      summaryData["solar_meter"] =
        summaryData["solar_meter"] + (reportData[key]["solar_meter"] || 0);
      summaryData["load_meter"] =
        summaryData["load_meter"] + (reportData[key]["load_meter"] || 0);
      summaryData["import_energy_meter"] =
        summaryData["import_energy_meter"] +
        (reportData[key]["import_energy_meter"] || 0);
      summaryData["export_energy_meter"] =
        summaryData["export_energy_meter"] +
        (reportData[key]["export_energy_meter"] || 0);
    });

    tableData.push([
      [<b key="summary">Sum</b>],
      [
        <b key="summary-solar_meter">{summaryData["solar_meter"].toFixed(2)}</b>
      ],
      [<b key="summary-load_meter">{summaryData["load_meter"].toFixed(2)}</b>],
      [
        <b key="summary-import_energy_meter">
          {summaryData["import_energy_meter"].toFixed(2)}
        </b>
      ],
      [
        <b key="summary-export_energy_meter">
          {summaryData["export_energy_meter"].toFixed(2)}
        </b>
      ],
      ["-"],
      ["-"],
      ["-"],
      ["-"],
      ["-"]
    ]);

    return (
      <div className={classes.summaryDaysChart}>
        <h6>
          <strong>
            {`Last ${reportCount[this.state.tabIndex || 0]} ${
              reportTypes[this.state.tabIndex || 0]
            }s details:`}
          </strong>
        </h6>
        <Table
          tableHeaderColor="primary"
          tableHead={[
            `${reportTypes[this.state.tabIndex || 0]}`,
            "Generated (KWh)",
            "Consumed (KWh)",
            "Imported (KWh)",
            "Exported (KWh)",
            "Ideal (KWh)",
            "Missed (KWh)",
            "Bill (INR)",
            "CO2 Net (kg)",
            "Full Report"
          ]}
          tableData={tableData}
          coloredColls={[3]}
          colorsColls={["primary"]}
        />
      </div>
    );
  }

  renderYesterdaysReport(deviceId, reportData) {
    const { classes } = this.props;
    const reportTypes = ["Daily", "Weekly", "Monthly"];
    const reportPeriods = ["day", "week", "month"];
    const reportPeriod = reportPeriods[this.state.tabIndex || 0];
    const reportStatusName = this.state.reportStatusNames[reportPeriod];
    const showRefreshButton = this.isAdminUser();
    const showMonthlyActions = this.state.tabIndex === 2;

    let periodLabel = "Day";
    let inputType = "date";
    let selectionValue = this.state.selectedDay;
    let selectionField = "selectedDay";

    if (reportPeriod === "week") {
      periodLabel = "Week Start";
      inputType = "date";
      selectionValue = this.state.selectedWeekStart;
      selectionField = "selectedWeekStart";
    } else if (reportPeriod === "month") {
      periodLabel = "Month";
      inputType = "month";
      selectionValue = this.state.selectedMonth;
      selectionField = "selectedMonth";
    }

    return (
      <div>
        <CardHeader className={classes.cardHeaderHover}>
          <div className={classes.reportHeaderRow}>
            <h3>
              <b>{`${reportTypes[this.state.tabIndex || 0]} report for ${deviceId}${reportStatusName ? ` - ${reportStatusName}` : ""}`}</b>
            </h3>
            <div className={classes.reportActions}>
              <div className={classes.periodSelector}>
                <span className={classes.periodLabel}>{periodLabel}</span>
                <input
                  className={classes.periodInput}
                  type={inputType}
                  value={selectionValue}
                  onChange={event =>
                    this.handlePeriodSelectionChange(selectionField, event.target.value)
                  }
                />
                <select
                  className={`${classes.periodInput} ${classes.actionSelect}`}
                  value={this.state.selectedAction}
                  onChange={event => this.handleActionSelectionChange(event.target.value)}
                >
                  <option value="view_selected_period">View Selected Period</option>
                  {showRefreshButton ? (
                    <option value="refresh_tab">Refresh This Tab</option>
                  ) : null}
                  {showRefreshButton ? (
                    <option value="force_refresh_tab">Force Refresh This Tab</option>
                  ) : null}
                  {showMonthlyActions ? (
                    <option value="open_monthly_html">Open Monthly HTML</option>
                  ) : null}
                  {showMonthlyActions ? (
                    <option value="force_open_monthly_html">Force Refresh Monthly HTML</option>
                  ) : null}
                </select>
                <Button
                  round
                  size="sm"
                  color="default"
                  disabled={
                    this.state.isRefreshingReports || this.isMonthlyReportInProgress()
                  }
                  onClick={() =>
                    this.runSelectedAction(reportPeriod, showRefreshButton, showMonthlyActions)
                  }
                >
                  Run Action
                </Button>
              </div>
              {showMonthlyActions ? this.renderMonthlyReportProgress(classes) : null}
              {this.state.refreshMessage ? (
                <p
                  className={`${classes.reportActionMessage} ${
                    this.state.refreshError ? classes.reportActionError : ""
                  }`}
                >
                  {this.state.refreshMessage}
                </p>
              ) : null}
              {this.state.monthlyReportMessage && !this.isMonthlyReportInProgress() ? (
                <p
                  className={`${classes.reportActionMessage} ${
                    this.state.monthlyReportError ? classes.reportActionError : ""
                  }`}
                >
                  {this.state.monthlyReportMessage}
                </p>
              ) : null}
              <p className={classes.reportActionMessage}>
                Force refresh actions regenerate underlying raw and HTML day files for the selected period.
              </p>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          {this.renderReportSummary(reportData)}
          {this.renderPeriodInsights(reportData)}
          {this.renderTrendSparklines(reportData)}
          {reportData.power_history_yesterday &&
            this.renderEnergyTimeChart(reportData.power_history_yesterday)}
          {this.renderEnergySummaryChart(reportData)}
          {reportData.per_day_energy_statistics &&
            this.renderLastDaysReportTable(
              reportData.per_day_energy_statistics.by_time
            )}
        </CardBody>
      </div>
    );
  }

  renderDeviceLoadInfo(deviceData) {
    const { classes } = this.props;
    const loadData = deviceData.loads_today;
    const currentLoad = deviceData.current_load || [];
    const currency = deviceData.properties.currency || "$";

    let loadInfo = Object.keys(loadData).map(loadName => {
      let data = { ...loadData[loadName] };
      currentLoad.forEach(loadInfo => {
        if (loadInfo.name === loadName) {
          data["currentlyRunning"] = loadInfo.qty;
          data["currentPower"] = loadInfo.power;
        } else {
          data["currentlyRunning"] = 0;
          data["currentPower"] = 0;
        }
      });
      data["name"] = loadName;
      return data;
    });

    return (
      loadData && (
        <GridContainer>
          <GridItem xs={12} sm={12} md={12}>
            <h3>
              <strong>{"Today's load details"}</strong>
            </h3>
            <Table
              tableHeaderColor="warning"
              tableHead={[
                "Load",
                "Currently Running",
                "Current Power",
                "Energy",
                "Amount",
                "Hours"
              ]}
              tableData={loadInfo.map(load => [
                <img
                  className={
                    load.currentlyRunning > 0
                      ? classes.loadOnImg
                      : classes.loadOffImg
                  }
                  key={load.name}
                  src={loadIcons[load.name]}
                  alt="..."
                />,
                load.currentlyRunning || 0,
                `${
                  load.currentPower
                    ? (load.currentPower / 1000.0).toFixed(1)
                    : "0"
                } KW`,
                `${load.energy.toFixed(1)} KWh`,
                `${load.money.toFixed(1)} ${currency}`,
                `${load.hours} hrs`
              ])}
              coloredColls={[3]}
              colorsColls={["primary"]}
            />
          </GridItem>
        </GridContainer>
      )
    );
  }

  render() {
    // eslint-disable-next-line no-unused-vars
    const { classes } = this.props;
    const { deviceData } = this.state;
    let deviceId = this.props.match.params && this.props.match.params.deviceId;
    if (deviceData === undefined) return null;
    return (
      <GridContainer key={`project-detail`}>
        <GridItem
          xs={12}
          sm={12}
          md={12}
          key={`project-${this.props.projectId}`}
        >
          <Card chart className={classes.cardHover}>
            <CardBody>
              <NavPills
                color="warning"
                onChange={this.tabChanged}
                vertical={{
                  tabsGrid: { xs: 12, sm: 12, md: 12 },
                  contentGrid: { xs: 12, sm: 12, md: 12 }
                }}
                tabs={[
                  {
                    tabButton: "Daily",
                    //tabIcon: SettingsApplications,
                    tabContent: this.renderYesterdaysReport(
                      deviceId,
                      deviceData
                    )
                  },
                  {
                    tabButton: "Weekly",
                    //tabIcon: SettingsApplications,
                    tabContent: this.renderYesterdaysReport(
                      deviceId,
                      deviceData
                    )
                  },
                  {
                    tabButton: "Monthly",
                    //tabIcon: SettingsApplications,
                    tabContent: this.renderYesterdaysReport(
                      deviceId,
                      deviceData
                    )
                  }
                ]}
              />
            </CardBody>
            <CardFooter stats>
              <a href="/dashboard" className={classes.marginRight}>
                Home
              </a>
              <a
                href={`/devicedetails/${deviceId}/`}
                className={classes.marginRight}
              >
                Details
              </a>
            </CardFooter>
          </Card>
        </GridItem>
      </GridContainer>
    );
  }
}

DeviceReports.propTypes = {
  classes: PropTypes.object.isRequired,
  axios: PropTypes.object,
  user: PropTypes.object
};

export default withStyles(styles)(DeviceReports);
